import { lazy, memo, Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";

const ReCAPTCHA = lazy(() => import("react-google-recaptcha"));
const SERVER_BASE_URL = "https://admin.monsinidress.com";
const DEFAULT_DATA = { PROM: { years: [2027, 2026, 2025] } };
let footerCache = null;

const ChevronIcon = ({ open }) => (
	<svg viewBox="0 0 20 20" fill="currentColor" className={`h-5 w-5 transition ${open ? "rotate-180" : ""}`} aria-hidden="true" focusable="false">
		<path fillRule="evenodd" d="M5.22 8.22a.75.75 0 0 1 1.06 0L10 11.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 9.28a.75.75 0 0 1 0-1.06Z" clipRule="evenodd" />
	</svg>
);

const MailIcon = () => (
	<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-5 w-5" aria-hidden="true" focusable="false">
		<rect x="3" y="5" width="18" height="14" rx="2" /><path d="m3 7 9 6 9-6" />
	</svg>
);

function normalizeFooterData(rawData) {
	if (!rawData || typeof rawData !== "object") return DEFAULT_DATA;
	const transformed = {};
	Object.entries(rawData).forEach(([name, value]) => {
		const key = String(name).toUpperCase();
		transformed[key] = {
			years: Array.isArray(value?.years)
				? [...new Set(value.years)].sort((a, b) => Number(b) - Number(a))
				: [],
		};
	});
	return { ...DEFAULT_DATA, ...transformed };
}

function Footer() {
	const footerRef = useRef(null);
	const requestedRef = useRef(false);
	const [openMenu, setOpenMenu] = useState(null);
	const [footerData, setFooterData] = useState(footerCache || DEFAULT_DATA);
	const [email, setEmail] = useState("");
	const [emailError, setEmailError] = useState("");
	const [subscribeMessage, setSubscribeMessage] = useState(null);
	const [capVal, setCapVal] = useState(null);
	const [status, setStatus] = useState(null);
	const [showRecaptcha, setShowRecaptcha] = useState(false);

	useEffect(() => {
		const node = footerRef.current;
		if (!node || footerCache || !("IntersectionObserver" in window)) return undefined;
		let controller = null;
		let timer = null;

		const loadFooter = async () => {
			if (requestedRef.current) return;
			requestedRef.current = true;
			controller = new AbortController();
			timer = window.setTimeout(() => controller.abort(), 6000);
			try {
				const response = await fetch(`${SERVER_BASE_URL}/api/navbar`, {
					method: "POST",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify({ brand: "Azure" }),
					signal: controller.signal,
				});
				if (!response.ok) return;
				const result = await response.json();
				const normalized = normalizeFooterData(result?.data);
				footerCache = normalized;
				setFooterData(normalized);
			} catch {
				setFooterData(DEFAULT_DATA);
			} finally {
				if (timer) window.clearTimeout(timer);
			}
		};

		const observer = new IntersectionObserver(
			(entries) => {
				if (entries[0]?.isIntersecting) {
					observer.disconnect();
					loadFooter();
				}
			},
			{ rootMargin: "700px 0px", threshold: 0.01 }
		);
		observer.observe(node);

		return () => {
			observer.disconnect();
			if (timer) window.clearTimeout(timer);
			controller?.abort();
		};
	}, []);

	const categoryEntries = useMemo(() => Object.entries(footerData), [footerData]);
	const toggleMenu = useCallback((menu) => setOpenMenu((current) => (current === menu ? null : menu)), []);

	const handleSubscribe = useCallback(async () => {
		const cleanEmail = email.trim().toLowerCase();
		const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
		setEmailError("");
		setSubscribeMessage(null);
		setStatus(null);

		if (!cleanEmail) {
			setEmailError("Email is required");
			return;
		}
		if (!emailRegex.test(cleanEmail)) {
			setEmailError("Please enter a valid email address");
			return;
		}
		if (!showRecaptcha) {
			setShowRecaptcha(true);
			return;
		}
		if (!capVal) {
			setStatus("recaptcha-error");
			return;
		}

		const controller = new AbortController();
		const timer = window.setTimeout(() => controller.abort(), 7000);
		try {
			const response = await fetch(`${SERVER_BASE_URL}/api/subscriber`, {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ brandName: "Azure", email: cleanEmail }),
				signal: controller.signal,
			});
			if (!response.ok) {
				const errorData = await response.json().catch(() => ({}));
				throw new Error(errorData?.message || "Subscription failed. Please try again.");
			}
			setSubscribeMessage("Thank you for subscribing! Check your inbox.");
			setEmail("");
			setCapVal(null);
			setShowRecaptcha(false);
			window.setTimeout(() => setSubscribeMessage(null), 4000);
		} catch (error) {
			setSubscribeMessage(error?.message || "Subscription failed. Please try again.");
			window.setTimeout(() => setSubscribeMessage(null), 5000);
		} finally {
			window.clearTimeout(timer);
		}
	}, [capVal, email, showRecaptcha]);

	return (
		<>
			{subscribeMessage && (
				<div className={`fixed bottom-4 right-4 z-[1202] flex items-center gap-2 rounded-lg p-3 text-white shadow-xl ${subscribeMessage.toLowerCase().includes("failed") ? "bg-red-700" : "bg-green-700"}`} role="status" aria-live="polite">
					<MailIcon />{subscribeMessage}
				</div>
			)}

			<footer ref={footerRef} className="w-full overflow-x-hidden bg-[#1f4c94] text-white">
				<div className="grid w-full gap-5 border-b border-white/40 px-6 md:grid-cols-2 md:px-10 lg:grid-cols-4 lg:px-16">
					<section className="flex flex-col items-start justify-center md:px-4 md:py-2 lg:px-10 lg:py-6" aria-labelledby="footer-newsletter-title">
						<h2 id="footer-newsletter-title" className="my-4 font-lora text-3xl">Let's get in Touch</h2>
						<p className="font-poppins text-[14px]">Sign up for our newsletter and get news about latest products and discounts.</p>
						<label htmlFor="footer-email" className="sr-only">Email address</label>
						<input
							id="footer-email"
							type="email"
							name="email"
							autoComplete="email"
							placeholder="Enter Your Email Address..."
							value={email}
							onChange={(e) => setEmail(e.target.value)}
							aria-invalid={Boolean(emailError)}
							aria-describedby={emailError ? "footer-email-error" : undefined}
							className="my-2 w-full rounded-lg border-2 border-white bg-transparent px-3 py-2 font-poppins text-[16px] text-white outline-none placeholder:text-white focus:bg-white/10"
						/>
						{emailError && <p id="footer-email-error" role="alert" className="mt-1 rounded bg-red-800 p-1 text-sm text-white">{emailError}</p>}

						{showRecaptcha && (
							<div className="mt-2">
								<Suspense fallback={<span className="text-sm">Loading verification...</span>}>
									<ReCAPTCHA sitekey="6Lc8fiwsAAAAAEnGQbIZaoT-y5EPeaK87c03G494" onChange={setCapVal} />
								</Suspense>
								{status === "recaptcha-error" && <p role="alert" className="mt-2 rounded bg-red-800 p-2 text-sm text-white">Please complete the reCAPTCHA verification.</p>}
							</div>
						)}

						<button type="button" onClick={handleSubscribe} className="mt-4 w-full rounded bg-white px-4 py-2 font-semibold text-[#173d78] transition hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#1f4c94]">
							{showRecaptcha ? "Complete Subscription" : "Subscribe"}
						</button>
					</section>

					<section className="flex flex-col gap-2 py-6 font-Jost-Regular md:px-5 md:py-10 lg:py-14" aria-labelledby="footer-collections-title">
						<h2 id="footer-collections-title" className="mb-2 font-lora text-[20px] font-medium">Collections</h2>
						{categoryEntries.map(([categoryName, categoryData]) => {
							const menuKey = categoryName.toLowerCase();
							const yearsList = categoryData?.years || [];
							const isOpen = openMenu === menuKey;
							const dropdownId = `footer-menu-${menuKey}`;
							return (
								<div key={categoryName} className="font-poppins">
									<button type="button" onClick={() => toggleMenu(menuKey)} aria-expanded={isOpen} aria-controls={dropdownId} className="flex w-full items-center justify-between rounded px-2 py-1 transition hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white">
										<span>{categoryName}</span><ChevronIcon open={isOpen} />
									</button>
									<div id={dropdownId} className={`ml-4 mt-2 flex flex-col gap-1 overflow-hidden transition-all duration-300 ${isOpen ? "max-h-60 opacity-100" : "max-h-0 opacity-0"}`}>
										{yearsList.map((year) => (
											<Link key={year} to={`/${menuKey}/${year}`} aria-label={`${year} ${categoryName} Collection`} className="rounded px-2 py-1 hover:bg-white/10">{year} Collection</Link>
										))}
										<Link to={`/size-chart/${menuKey}`} aria-label={`${categoryName} Size Chart`} className="rounded px-2 py-1 hover:bg-white/10">Size Chart</Link>
									</div>
								</div>
							);
						})}
					</section>

					<section className="flex flex-col gap-2 py-6 font-Jost-Regular md:px-5 md:py-10 lg:py-14" aria-labelledby="footer-contact-title">
						<h2 id="footer-contact-title" className="mb-2 font-lora text-[20px] font-medium">Contact Links</h2>
						<Link to="/about" className="rounded px-2 py-1 font-poppins hover:bg-white/10">About Us</Link>
						<Link to="/contact" className="rounded px-2 py-1 font-poppins hover:bg-white/10">Contact Us</Link>
						<a href="https://market.azuredress.com/" target="_blank" rel="noopener noreferrer" className="mt-3 inline-block w-fit rounded-md border border-white px-4 py-2 font-bold text-white hover:bg-white/10">RETAILER LOGIN</a>
					</section>

					<section className="flex flex-col gap-2 py-6 md:px-5 md:py-10 lg:py-14" aria-labelledby="footer-social-title">
						<h2 id="footer-social-title" className="mb-2 font-lora text-[20px] font-medium">Social Links</h2>
						<a href="https://www.instagram.com/azuredress?igsh=MXhoNzZ2N3hqbjgyeA==" target="_blank" rel="noopener noreferrer" className="rounded px-2 py-1 font-poppins hover:bg-white/10">Instagram</a>
					</section>
				</div>
			</footer>
		</>
	);
}

export default memo(Footer);
